import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, FileText, Printer } from 'lucide-react';
import { exportCSV, exportXLSX, exportPDF, printData } from '../utils/exportUtils';

const StatementPage = () => {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterVendorPatient, setFilterVendorPatient] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [thenBy, setThenBy] = useState('');
  const [viewMode, setViewMode] = useState('statement');

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const res = await api.get('/billing/statement');
      setTransactions(res.data.transactions || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatAmount = (amount) => {
    const isCredit = amount >= 0;
    return (
      <span className={`font-medium ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
        {isCredit ? '+' : ''}₹{Math.abs(amount).toFixed(2)}
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const getSourceBadge = (source) => {
    const styles = {
      collection: 'bg-green-100 text-green-700',
      refund: 'bg-red-100 text-red-700',
      expense: 'bg-orange-100 text-orange-700',
      opening: 'bg-gray-200 text-gray-800'
    };
    const labels = {
      collection: 'Credit',
      refund: 'Debit',
      expense: 'Debit',
      opening: 'Opening'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[source] || 'bg-gray-100 text-gray-700'}`}>
        {labels[source] || source}
      </span>
    );
  };

  const vendorPatientOptions = useMemo(() => {
    const names = new Set();
    transactions.forEach(t => {
      const name = t.patient?.trim();
      if (name && name !== '-') names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  const allTransactionsWithBalance = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
    let runningBalance = 0;
    return sorted.map(t => {
      runningBalance += t.amount;
      return { ...t, runningBalance };
    });
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const filtered = allTransactionsWithBalance.filter(t => {
      if (filterType === 'credit' && t.amount < 0) return false;
      if (filterType === 'debit' && t.amount >= 0) return false;
      const tDate = t.date ? new Date(t.date) : null;
      if (filterMonth && tDate) {
        const [year, month] = filterMonth.split('-');
        if (tDate.getFullYear() !== parseInt(year) || tDate.getMonth() + 1 !== parseInt(month)) return false;
      }
      if (filterFrom && tDate) {
        const from = new Date(filterFrom);
        from.setHours(0, 0, 0, 0);
        if (tDate < from) return false;
      }
      if (filterTo && tDate) {
        const to = new Date(filterTo);
        to.setHours(23, 59, 59, 999);
        if (tDate > to) return false;
      }
      if (filterVendorPatient && t.patient !== filterVendorPatient) return false;
      return true;
    });
    const compareBy = (field, a, b) => {
      if (field === 'date-desc') return new Date(b.date || 0) - new Date(a.date || 0);
      if (field === 'date-asc') return new Date(a.date || 0) - new Date(b.date || 0);
      const nameA = (a.patient || '').toLowerCase();
      const nameB = (b.patient || '').toLowerCase();
      if (field === 'name-asc') return nameA.localeCompare(nameB);
      if (field === 'name-desc') return nameB.localeCompare(nameA);
      return 0;
    };
    const sorted = [...filtered].sort((a, b) => {
      const primary = compareBy(sortBy, a, b);
      if (primary !== 0) return primary;
      if (thenBy && thenBy !== sortBy) return compareBy(thenBy, a, b);
      return 0;
    });

    let startDate = null;
    if (filterMonth) {
      const [year, month] = filterMonth.split('-');
      startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    } else if (filterFrom) {
      startDate = new Date(filterFrom);
      startDate.setHours(0, 0, 0, 0);
    }

    if (!startDate) {
      return sorted;
    }

    const previous = allTransactionsWithBalance.filter(t => new Date(t.date || 0) < startDate);
    const openingBalance = previous.length ? previous[previous.length - 1].runningBalance : 0;

    const openingDate = new Date(startDate);
    openingDate.setDate(openingDate.getDate() - 1);
    const y = openingDate.getFullYear();
    const m = String(openingDate.getMonth() + 1).padStart(2, '0');
    const d = String(openingDate.getDate()).padStart(2, '0');

    const openingRow = {
      id: 'opening-balance',
      date: `${y}-${m}-${d}`,
      category: 'Opening Balance',
      source: 'opening',
      billNumber: '-',
      patient: '',
      paymentMethod: '',
      amount: 0,
      runningBalance: openingBalance
    };

    return [openingRow, ...sorted];
  }, [allTransactionsWithBalance, filterType, filterMonth, filterFrom, filterTo, filterVendorPatient, sortBy, thenBy]);

  const patientGroups = useMemo(() => {
    const map = new Map();
    filteredTransactions
      .filter(t => t.source !== 'opening')
      .forEach(t => {
        const name = (t.patient || '').trim() || 'Unknown';
        if (!map.has(name)) map.set(name, []);
        map.get(name).push(t);
      });
    const names = Array.from(map.keys()).sort((a, b) =>
      sortBy === 'name-desc' ? b.localeCompare(a) : a.localeCompare(b)
    );
    return names.map(name => {
      const items = map.get(name);
      const total = items.reduce((sum, t) => sum + t.amount, 0);
      const totalCredit = items.reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);
      const totalDebit = items.reduce((sum, t) => sum + (t.amount < 0 ? t.amount : 0), 0);
      return { name, items, total, totalCredit, totalDebit };
    });
  }, [filteredTransactions, sortBy]);

  const patientExportRows = useMemo(() => {
    const rows = [];
    patientGroups.forEach(group => {
      rows.push(...group.items);
      rows.push({
        id: `total-${group.name}`,
        date: '',
        category: '',
        source: 'collection',
        billNumber: '',
        patient: `Total — ${group.name}`,
        paymentMethod: '',
        amount: group.total,
        runningBalance: null,
        isSubtotal: true
      });
    });
    return rows;
  }, [patientGroups]);

  const filteredSummary = useMemo(() => {
    const totalCredit = filteredTransactions.reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);
    const totalDebit = filteredTransactions.reduce((sum, t) => sum + (t.amount < 0 ? t.amount : 0), 0);
    return { totalCredit, totalDebit, netBalance: totalCredit + totalDebit };
  }, [filteredTransactions]);

  const reportSubtitle = useMemo(() => {
    const filters = [];
    if (filterType !== 'all') filters.push(`Type: ${filterType.charAt(0).toUpperCase() + filterType.slice(1)}`);
    if (filterMonth) filters.push(`Month: ${filterMonth}`);
    if (filterFrom) filters.push(`From: ${formatDate(filterFrom)}`);
    if (filterTo) filters.push(`To: ${formatDate(filterTo)}`);
    if (filterVendorPatient) filters.push(`Patient / Vendor: ${filterVendorPatient}`);
    const sortLabels = {
      'date-desc': 'Date (Newest)',
      'date-asc': 'Date (Oldest)',
      'name-asc': 'Name (A-Z)',
      'name-desc': 'Name (Z-A)',
      '': 'None'
    };
    const sortText = thenBy ? `${sortLabels[sortBy]}, then ${sortLabels[thenBy]}` : sortLabels[sortBy];
    filters.push(`Sort: ${sortText}`);
    if (viewMode === 'patient') filters.push('View: Patient Wise');
    const filterLine = filters.join(' | ');
    const summaryLine = `Total Collection: ₹${filteredSummary.totalCredit.toFixed(2)} | Total Debit: ₹${Math.abs(filteredSummary.totalDebit).toFixed(2)} | Net Balance: ₹${filteredSummary.netBalance.toFixed(2)}`;
    return `${filterLine}\n${summaryLine}`;
  }, [filterType, filterMonth, filterFrom, filterTo, filterVendorPatient, sortBy, thenBy, filteredSummary, viewMode]);

  const resetFilters = () => {
    setFilterType('all');
    setFilterMonth('');
    setFilterFrom('');
    setFilterTo('');
    setFilterVendorPatient('');
    setSortBy('date-desc');
    setThenBy('');
    setViewMode('statement');
  };

  const statementExportColumns = [
    { header: 'Date', accessor: (t) => formatDate(t.date) },
    { header: 'Category', accessor: (t) => (t.category ? t.category.charAt(0).toUpperCase() + t.category.slice(1) : '') },
    { header: 'Source', accessor: (t) => {
      if (t.source === 'collection') return 'Credit';
      if (t.source === 'opening') return 'Opening';
      return 'Debit';
    } },
    { header: 'Bill / Ref', accessor: (t) => t.billNumber || '-' },
    { header: 'Patient / Vendor', key: 'patient' },
    { header: 'Method', accessor: (t) => (t.paymentMethod ? t.paymentMethod.toUpperCase() : '-') },
    { header: 'Amount', accessor: (t) => `${t.amount >= 0 ? '+' : '-'}₹${Math.abs(t.amount).toFixed(2)}`, align: 'right' },
    { header: 'Balance', accessor: (t) => t.runningBalance != null ? `₹${t.runningBalance.toFixed(2)}` : '', align: 'right' }
  ];

  const handleExport = (format) => {
    const filename = `statement-${new Date().toISOString().split('T')[0]}`;
    const data = viewMode === 'patient' ? patientExportRows : filteredTransactions;
    if (format === 'csv') exportCSV(data, filename, statementExportColumns);
    else if (format === 'xlsx') exportXLSX(data, filename, statementExportColumns);
    else if (format === 'pdf') exportPDF(data, filename, statementExportColumns, 'Accounts Statement', reportSubtitle);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <button onClick={() => window.history.back()} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <FileText className="h-6 w-6 mr-2" />
              Accounts Statement
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="form-grid form-grid-3 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Total Collection</p>
            <p className="text-2xl font-bold text-green-600">₹{filteredSummary.totalCredit.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Total Debit</p>
            <p className="text-2xl font-bold text-red-600">₹{Math.abs(filteredSummary.totalDebit).toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Net Balance</p>
            <p className={`text-2xl font-bold ${filteredSummary.netBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ₹{filteredSummary.netBalance.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="form-grid form-grid-3 mb-4">
            <div>
              <label className="form-label">Type</label>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="form-input w-full">
                <option value="all">All</option>
                <option value="credit">Credit</option>
                <option value="debit">Debit</option>
              </select>
            </div>
            <div>
              <label className="form-label">Month</label>
              <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="form-input w-full" />
            </div>
            <div>
              <label className="form-label">From Date</label>
              <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="form-input w-full" />
            </div>
            <div>
              <label className="form-label">To Date</label>
              <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="form-input w-full" />
            </div>
            <div>
              <label className="form-label">Patient / Vendor</label>
              <select value={filterVendorPatient} onChange={(e) => setFilterVendorPatient(e.target.value)} className="form-input w-full">
                <option value="">All</option>
                {vendorPatientOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Sort By</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="form-input w-full">
                <option value="date-desc">Date (Newest)</option>
                <option value="date-asc">Date (Oldest)</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
              </select>
            </div>
            <div>
              <label className="form-label">Then By</label>
              <select value={thenBy} onChange={(e) => setThenBy(e.target.value)} className="form-input w-full">
                <option value="">None</option>
                <option value="date-desc">Date (Newest)</option>
                <option value="date-asc">Date (Oldest)</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
              </select>
            </div>
            <div>
              <label className="form-label">View</label>
              <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} className="form-input w-full">
                <option value="statement">Statement</option>
                <option value="patient">Patient Wise</option>
              </select>
            </div>
          </div>
          <button type="button" onClick={resetFilters} className="btn btn-secondary text-sm">Reset Filters</button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Statement</h2>
              <div className="flex items-center space-x-2">
                <select
                  onChange={(e) => {
                    const format = e.target.value;
                    if (format) handleExport(format);
                    e.target.value = '';
                  }}
                  className="form-input text-sm"
                  defaultValue=""
                >
                  <option value="" disabled>Export</option>
                  <option value="csv">CSV</option>
                  <option value="xlsx">XLSX</option>
                  <option value="pdf">PDF</option>
                </select>
                <button
                  onClick={() => printData(viewMode === 'patient' ? patientExportRows : filteredTransactions, statementExportColumns, 'Accounts Statement', reportSubtitle)}
                  className="bg-gray-600 text-white p-2 rounded hover:bg-gray-700"
                  title="Print"
                >
                  <Printer className="h-4 w-4" />
                </button>
              </div>
            </div>
            {loading ? (
              <p className="text-center py-8 text-gray-500">Loading...</p>
            ) : filteredTransactions.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No records found</p>
            ) : viewMode === 'patient' ? (
              <div className="space-y-6">
                {patientGroups.map((group) => (
                  <div key={group.name} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-3 font-semibold text-gray-900">
                      Patient Name: {group.name}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-2 px-4">Date of Bill</th>
                            <th className="text-left py-2 px-4">Bill Type</th>
                            <th className="text-left py-2 px-4">Bill / Ref</th>
                            <th className="text-left py-2 px-4">Mode of Payment</th>
                            <th className="text-right py-2 px-4">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map((t) => (
                            <tr key={t.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-4">{formatDate(t.date)}</td>
                              <td className="py-2 px-4 capitalize">{t.category}</td>
                              <td className="py-2 px-4 font-mono text-sm">{t.billNumber || '-'}</td>
                              <td className="py-2 px-4 capitalize">{t.paymentMethod || '-'}</td>
                              <td className="py-2 px-4 text-right">{formatAmount(t.amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-gray-50 font-semibold">
                            <td colSpan="4" className="py-2 px-4 text-right">Total</td>
                            <td className={`py-2 px-4 text-right ${group.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ₹{group.total.toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Category</th>
                      <th className="text-left py-3 px-4">Source</th>
                      <th className="text-left py-3 px-4">Bill / Ref</th>
                      <th className="text-left py-3 px-4">Patient / Vendor</th>
                      <th className="text-left py-3 px-4">Method</th>
                      <th className="text-right py-3 px-4">Amount</th>
                      <th className="text-right py-3 px-4">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((t) => (
                      <tr key={t.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{formatDate(t.date)}</td>
                        <td className="py-3 px-4 capitalize">{t.category}</td>
                        <td className="py-3 px-4">{getSourceBadge(t.source)}</td>
                        <td className="py-3 px-4 font-mono text-sm">{t.billNumber || '-'}</td>
                        <td className="py-3 px-4">{t.patient}</td>
                        <td className="py-3 px-4 capitalize">{t.paymentMethod}</td>
                        <td className="py-3 px-4 text-right">{formatAmount(t.amount)}</td>
                        <td className={`py-3 px-4 text-right font-medium ${t.runningBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>₹{t.runningBalance.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatementPage;